import React, { useMemo } from 'react';
import PageTop from '../../../core/components/PageTop';
import Panel from '../../../core/components/Panel';
import TabsPanel from '../../../core/components/TabsPanel';
import DataMonitorFacilitySelect from './components/DataMonitorFacilitySelect';
import { useDataMonitorInit } from './pagehooks/useDataMonitorInit';
import { useDataMonitorStore } from './pagehooks/useDataMonitorStore';
import MonitorStatusTable from './table/MonitorStatusTable';
import MonitorStatusVerityTable from './table/MonitorStatusVerityTable';
import ProcessingErrorsTable from './table/ProcessingErrorsTable';
import MonitorDefaultsTable from './table/MonitorDefaultsTable';
import SystemTab from './system/SystemTab';
import ActivityTab from './activity/ActivityTab';
import WacOrderingControl from './wac/WacOrderingControl';
import { ngContext } from '../../../core/services/ng.delegate';
import utilSvc from '../../../core/utilSvc';
import { DataMonitorTab } from '../datamonitor_const';

const PageOpsDataMonitor = (): JSX.Element => {
    return (
        <>
            <PageTop
                title="Data Monitor"
                icon="sprite-monitor"
            />
            <DataMonitorContent />
        </>
    );
};

const DataMonitorContent = (): JSX.Element => {
    const ready = useDataMonitorInit();

    if (!ready) return <></>;

    return <DataMonitorBody />;
};

const DataMonitorBody = (): JSX.Element => {
    const { activeTab, facilityValid, setActiveTab } = useDataMonitorStore();
    const ctx = ngContext();
    const isVerity = ctx.User?.Type === 'VERITY' || ctx.User?.Type === 'VERITYSA';
    const isVeritySA = ctx.User?.Type === 'VERITYSA';
    const isCDC = utilSvc.user.hasFeature(ctx.User, 'report.wacneut');

    const isVeriSaveOnly = !!ctx.User?.VeriSaveOnly;

    const tabs = useMemo(() => {
        const list: { id: DataMonitorTab; label: string; content: JSX.Element }[] = [];

        // System + Defaults are VERITY-only; System is built (ENT-27282), Defaults pending (ENT-27324)
        if (isVerity) {
            list.push({
                id: 'SYSTEM',
                label: 'System',
                content: <SystemTab />,
            });
        }

        list.push({
            id: 'MONITORS',
            label: 'Monitor Status',
            content: isVerity ? (
                <MonitorStatusVerityTable isVeritySA={isVeritySA} />
            ) : (
                <>
                    <DataMonitorFacilitySelect />
                    {facilityValid ? (
                        <MonitorStatusTable
                            isCDC={isCDC}
                            isVeriSaveOnly={isVeriSaveOnly}
                        />
                    ) : null}
                </>
            ),
        });

        list.push({
            id: 'PROC',
            label: 'Processing Errors',
            content: isVerity ? (
                <ProcessingErrorsTable isVerity />
            ) : (
                <>
                    <DataMonitorFacilitySelect />
                    {facilityValid ? <ProcessingErrorsTable /> : null}
                </>
            ),
        });

        if (isVerity) {
            list.push({
                id: 'DEFAULTS',
                label: 'Defaults',
                content: <MonitorDefaultsTable canEdit={isVeritySA} />,
            });
            list.push({
                id: 'ACTIVITY',
                label: 'Activity',
                content: <ActivityTab />,
            });
        }

        return list;
    }, [isVerity, isVeritySA, isCDC, isVeriSaveOnly, facilityValid]);

    return (
        <div className="datamonitor-feature datamonitor-list">
            <Panel
                flexDirection="column"
                flushTabs
            >
                <TabsPanel
                    tabs={tabs}
                    selectedId={activeTab}
                    onTabChange={(id): void => setActiveTab(id as DataMonitorTab)}
                    topRight={isVeritySA ? <WacOrderingControl /> : undefined}
                />
            </Panel>
        </div>
    );
};

export default PageOpsDataMonitor;
